import React from 'react';
import styled from 'styled-components';
import { Trans } from '@lingui/macro';
import { GroupSeparator } from '@toqio/toqio-web-components';
import { SecondaryButton } from '../../../shared/components';
import type { PaymentRow } from '../types';
import { StatusChip } from './StatusChip';
import { formatGroupDate, formatRowAmount, formatRowTime, GREY, INK } from './styles';

/**
 * Date-grouped payment list (PRD §4b, MAPPINGS [Table row / list]). The
 * contract returns a flat `data[]`; the FE groups rows by calendar day of
 * `requestDate` (first-seen order preserved) and renders a `GroupSeparator`
 * above each group.
 *
 * The row is a bespoke styled grid that mirrors the live deployed `TableRow`
 * exactly (3 equal columns, 77px tall, thin divider). We don't use the DS
 * `TableRow` here: its grid layout is theme-driven and does not render under
 * forms-host's styled-components setup (it collapses to `display:block`), so a
 * bespoke row — like `card-dashboard`'s — is the reliable, pixel-accurate
 * choice (MAPPINGS [Table row / list] note: "a bespoke styled row when the
 * design fights the DS defaults").
 */
const Group = styled.div`
  display: flex;
  flex-direction: column;
`;

// Below this width the live app reflows the row to two columns (ID/ref left,
// amount-over-status right) and hides the client/time column — matched here.
const MOBILE = '767px';

const Row = styled.button`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  align-items: center;
  gap: 16px;
  width: 100%;
  min-height: 77px;
  padding: 12px;
  border: none;
  border-bottom: 1px solid #f2f2f2;
  border-radius: 8px;
  background: none;
  cursor: pointer;
  text-align: left;
  font-family: Inter, sans-serif;

  &:hover {
    background: #fafafa;
  }

  @media (max-width: ${MOBILE}) {
    grid-template-columns: 1fr auto;
  }
`;

const Cell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

/** col2 (client + time): hidden on mobile, like the live app. */
const ClientCell = styled(Cell)`
  @media (max-width: ${MOBILE}) {
    display: none;
  }
`;

const Title = styled.span`
  font:
    600 16px/24px Inter,
    sans-serif;
  color: ${INK};
`;

const Sub = styled.span`
  font:
    400 14px/20px Inter,
    sans-serif;
  color: ${INK};
`;

const Client = styled.span`
  font:
    400 16px/24px Inter,
    sans-serif;
  color: ${INK};
`;

const Time = styled.span`
  font:
    400 14px/20px Inter,
    sans-serif;
  color: ${GREY};
`;

/**
 * col3: chip on the left, amount on the right (live `space-between` flex). On
 * mobile the live app stacks them right-aligned with the amount on top, so we
 * switch to `column-reverse` (children render chip-then-amount in source).
 */
const ChipAmount = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  @media (max-width: ${MOBILE}) {
    flex-direction: column-reverse;
    align-items: flex-end;
    justify-content: center;
    gap: 4px;
  }
`;

const Amount = styled.span`
  font:
    400 16px/24px Inter,
    sans-serif;
  color: ${INK};
  white-space: nowrap;
`;

const Loader = styled.div`
  display: flex;
  justify-content: center;
  padding: 16px 0;
`;

type DayGroup = { key: string; label: string; rows: PaymentRow[] };

const groupByDay = (rows: PaymentRow[], locale: string): DayGroup[] => {
  const groups: DayGroup[] = [];
  const index = new Map<string, DayGroup>();
  for (const row of rows) {
    const label = formatGroupDate(row.requestDate, locale);
    let group = index.get(label);
    if (!group) {
      group = { key: label, label, rows: [] };
      index.set(label, group);
      groups.push(group);
    }
    group.rows.push(row);
  }
  return groups;
};

type Props = {
  rows: PaymentRow[];
  locale: string;
  hasNext: boolean;
  loadingMore: boolean;
  onOpen: (revenueId: string) => void;
  onLoadMore: () => void;
};

export const PaymentList: React.FC<Props> = ({
  rows,
  locale,
  hasNext,
  loadingMore,
  onOpen,
  onLoadMore,
}) => (
  <div>
    {groupByDay(rows, locale).map((group) => (
      <Group key={group.key}>
        <GroupSeparator label={group.label} />
        {group.rows.map((row) => (
          <Row key={row.id} type="button" onClick={() => onOpen(row.id)}>
            <Cell>
              <Title>{row.externalClientId}</Title>
              {row.concept && <Sub>{row.concept}</Sub>}
            </Cell>
            <ClientCell>
              <Client>{row.clientName}</Client>
              <Time>{formatRowTime(row.requestDate, locale)}</Time>
            </ClientCell>
            <ChipAmount>
              <StatusChip status={row.status} />
              <Amount>
                {formatRowAmount(row.invoiceAmount)} {row.invoiceCurrency}
              </Amount>
            </ChipAmount>
          </Row>
        ))}
      </Group>
    ))}
    {hasNext && (
      <Loader>
        <SecondaryButton onClick={onLoadMore} disabled={loadingMore}>
          <Trans>Show more</Trans>
        </SecondaryButton>
      </Loader>
    )}
  </div>
);
